/**
 * Real-world US trucking language for in-app lookup.
 * Keep definitions short; the published docs page is the long-form source.
 */

export const TRUCKING_DICTIONARY_PATH = "/help/dictionary";

export const TRUCKING_DICTIONARY_DOCS = {
  docsPluginId: "customer",
  docsSlug: "industry-guides/trucking-language-dictionary",
};

export const TRUCKING_DICTIONARY_CATEGORIES = Object.freeze([
  "People and companies",
  "Equipment",
  "Freight",
  "Documents",
  "Money",
  "Time and hours",
  "Safety and compliance",
  "Operations",
  "Status",
]);

/**
 * @typedef {object} TruckingDictionaryTerm
 * @property {string} id
 * @property {string} term
 * @property {string[]} [aliases]
 * @property {string} category
 * @property {string} definition
 */

/** @type {TruckingDictionaryTerm[]} */
export const TRUCKING_DICTIONARY_TERMS = [
  {
    id: "broker",
    term: "Broker",
    category: "People and companies",
    definition:
      "Arranges freight between a shipper and a carrier. Usually does not own the truck.",
  },
  {
    id: "carrier",
    term: "Carrier",
    aliases: ["motor carrier"],
    category: "People and companies",
    definition:
      "The trucking company responsible for moving the freight. Identified by a DOT number and often an MC number.",
  },
  {
    id: "consignee",
    term: "Consignee",
    aliases: ["receiver"],
    category: "People and companies",
    definition: "The company that receives the freight. Delivery happens here.",
  },
  {
    id: "dispatcher",
    term: "Dispatcher",
    aliases: ["dispatch"],
    category: "People and companies",
    definition:
      "Assigns loads, trucks, and drivers, then stays on the phone when plans change.",
  },
  {
    id: "owner-operator",
    term: "Owner-operator",
    aliases: ["O/O", "OO"],
    category: "People and companies",
    definition:
      "A driver who owns or leases their own truck and hauls for a carrier or directly for freight.",
  },
  {
    id: "shipper",
    term: "Shipper",
    category: "People and companies",
    definition:
      "The company that tenders freight at pickup. Usually the first dock you back into.",
  },
  {
    id: "3pl",
    term: "3PL",
    aliases: ["third-party logistics"],
    category: "People and companies",
    definition:
      "A company that manages freight or warehousing for a shipper. Often a broker plus extra services.",
  },
  {
    id: "tractor",
    term: "Tractor",
    aliases: ["truck", "power unit", "cab"],
    category: "Equipment",
    definition:
      "The front unit with the engine. It pulls the trailer. Yard lists often say power unit.",
  },
  {
    id: "trailer",
    term: "Trailer",
    aliases: ["dry van", "reefer", "flatbed", "tanker"],
    category: "Equipment",
    definition:
      "The box or bed that holds freight. Common types: dry van, reefer, flatbed, step deck, tanker.",
  },
  {
    id: "bobtail",
    term: "Bobtail",
    category: "Equipment",
    definition: "Driving the tractor with no trailer. Not the same as deadhead.",
  },
  {
    id: "power-only",
    term: "Power only",
    category: "Equipment",
    definition:
      "The carrier supplies a tractor and driver to pull someone else's trailer.",
  },
  {
    id: "load",
    term: "Load",
    aliases: ["shipment", "freight"],
    category: "Freight",
    definition:
      "One contracted move: pickup, delivery, commodity, and pay. Not necessarily a single stop.",
  },
  {
    id: "ftl",
    term: "FTL",
    aliases: ["TL", "full truckload", "truckload"],
    category: "Freight",
    definition:
      "The shipper pays for the whole trailer. Typical: one pickup, one delivery.",
  },
  {
    id: "ltl",
    term: "LTL",
    aliases: ["less-than-truckload"],
    category: "Freight",
    definition:
      "Freight that does not fill a trailer. Several shippers share space; more stops and handling.",
  },
  {
    id: "hazmat",
    term: "Hazmat",
    aliases: ["hazardous materials", "DG"],
    category: "Freight",
    definition:
      "Freight that can harm people or the environment. Needs a trained driver and extra documents.",
  },
  {
    id: "deadhead",
    term: "Deadhead",
    aliases: ["empty miles"],
    category: "Freight",
    definition:
      "Miles driven with an empty trailer (or no paying freight) to reach the next pickup.",
  },
  {
    id: "lane",
    term: "Lane",
    category: "Freight",
    definition: "A repeated origin–destination pair, like Dallas to Atlanta.",
  },
  {
    id: "commodity",
    term: "Commodity",
    category: "Freight",
    definition:
      "What is actually on the trailer. Needed for rates, claims, and hazmat.",
  },
  {
    id: "bol",
    term: "BOL",
    aliases: ["bill of lading", "eBOL", "electronic BOL"],
    category: "Documents",
    definition:
      "The main shipping document: shipper, consignee, commodity, count/weight, and who is responsible.",
  },
  {
    id: "pod",
    term: "POD",
    aliases: ["proof of delivery"],
    category: "Documents",
    definition:
      "Evidence the consignee received the freight: signature, stamp, photos, or a signed BOL at delivery.",
  },
  {
    id: "pro",
    term: "PRO number",
    aliases: ["PRO"],
    category: "Documents",
    definition: "The carrier's tracking number for that shipment.",
  },
  {
    id: "rate-con",
    term: "Rate confirmation",
    aliases: ["rate con"],
    category: "Documents",
    definition:
      "The broker's or shipper's written offer: miles, rate, pickup/delivery, and notes.",
  },
  {
    id: "seal",
    term: "Seal",
    aliases: ["seal number"],
    category: "Documents",
    definition:
      "A numbered seal on trailer doors. A broken seal at delivery is a red flag. Write the number on the BOL.",
  },
  {
    id: "linehaul",
    term: "Linehaul",
    category: "Money",
    definition: "Base pay for moving the freight, before extras.",
  },
  {
    id: "fsc",
    term: "Fuel surcharge",
    aliases: ["FSC"],
    category: "Money",
    definition: "Extra pay that moves with diesel prices. Often a separate line from linehaul.",
  },
  {
    id: "accessorial",
    term: "Accessorial",
    category: "Money",
    definition:
      "Anything billed beyond linehaul: detention, layover, lumper, extra stop, tarping.",
  },
  {
    id: "detention",
    term: "Detention",
    aliases: ["dwell"],
    category: "Money",
    definition:
      "Pay when the driver is held at shipper or receiver past the free time in the rate con.",
  },
  {
    id: "lumper",
    term: "Lumper",
    category: "Money",
    definition:
      "A person or company paid to load or unload so the driver does not. Often reimbursed with a receipt.",
  },
  {
    id: "tonu",
    term: "TONU",
    aliases: ["truck ordered not used"],
    category: "Money",
    definition:
      "The truck was booked and the freight cancelled. Carriers bill a TONU fee.",
  },
  {
    id: "settlement",
    term: "Settlement",
    category: "Money",
    definition:
      "The driver's pay statement: miles, load pay, deductions, reimbursements.",
  },
  {
    id: "appointment",
    term: "Appointment",
    aliases: ["appt", "FCFS", "first come first served"],
    category: "Time and hours",
    definition:
      "A booked arrival time. FCFS means no appointment — you wait in line.",
  },
  {
    id: "hos",
    term: "Hours of Service",
    aliases: ["HOS", "11-hour", "14-hour", "70-hour"],
    category: "Time and hours",
    definition:
      "Federal limits on driving and on-duty time. Breaking HOS can put the truck out of service.",
  },
  {
    id: "eld",
    term: "ELD",
    aliases: ["electronic logging device"],
    category: "Time and hours",
    definition: "The device or app that records driving time for HOS.",
  },
  {
    id: "cdl",
    term: "CDL",
    aliases: ["commercial driver's license"],
    category: "Safety and compliance",
    definition:
      "The license required to drive a commercial truck. Endorsements add hazmat, tanker, or doubles.",
  },
  {
    id: "dot",
    term: "DOT number",
    aliases: ["DOT", "MC", "MC number"],
    category: "Safety and compliance",
    definition:
      "Federal ID for a carrier. MC authority is what lets many for-hire carriers haul across state lines.",
  },
  {
    id: "csa",
    term: "CSA",
    aliases: ["FMCSA"],
    category: "Safety and compliance",
    definition:
      "FMCSA safety scoring from inspections, crashes, and violations. High scores make freight and insurance harder.",
  },
  {
    id: "oos",
    term: "Out of service",
    aliases: ["OOS"],
    category: "Safety and compliance",
    definition:
      "An inspector or the company has taken the driver or truck off the road until a violation is fixed.",
  },
  {
    id: "twic",
    term: "TWIC",
    category: "Safety and compliance",
    definition:
      "Credential needed for many port and maritime facilities.",
  },
  {
    id: "drop-and-hook",
    term: "Drop and hook",
    category: "Operations",
    definition:
      "Leave a trailer, hook to another, and leave. Faster than waiting for a live load or unload.",
  },
  {
    id: "live-load",
    term: "Live load / live unload",
    aliases: ["live load", "live unload"],
    category: "Operations",
    definition:
      "The driver waits while the facility loads or unloads that trailer. This is where detention happens.",
  },
  {
    id: "osd",
    term: "OS&D",
    aliases: ["overage shortage damage", "claim"],
    category: "Operations",
    definition:
      "Freight count is high, low, or damaged. Note it on the BOL before you leave the dock.",
  },
  {
    id: "tender",
    term: "Tender",
    aliases: ["book", "cover"],
    category: "Operations",
    definition:
      "Tender is offering the load. Book is accepting it. Cover is finding a truck for an open load.",
  },
  {
    id: "en-route",
    term: "En route",
    aliases: ["rolling", "in transit", "dispatched", "loaded", "delivered", "fall off"],
    category: "Status",
    definition:
      "Spoken status: dispatched (released to run), rolling (moving), loaded (freight on), delivered (POD in hand), fall off (the truck dropped the load).",
  },
];

const DICTIONARY_TERM_KEYWORDS = Array.from(
  new Set(
    TRUCKING_DICTIONARY_TERMS.flatMap((item) => [
      item.term,
      ...(item.aliases || []),
    ])
  )
);

/**
 * Keywords used by Help Hub search so "BOL" / "POD" / "HOS" find this guide.
 * @type {string[]}
 */
export const TRUCKING_DICTIONARY_SEARCH_KEYWORDS = [
  "dictionary",
  "glossary",
  ...DICTIONARY_TERM_KEYWORDS,
];

/**
 * @param {string} [query]
 * @param {{ category?: string }} [options]
 * @returns {TruckingDictionaryTerm[]}
 */
export function searchDictionaryTerms(query, options = {}) {
  const category = options.category;
  const search = String(query || "")
    .toLowerCase()
    .trim();

  return TRUCKING_DICTIONARY_TERMS.filter((item) => {
    if (category && item.category !== category) return false;
    if (!search) return true;
    const haystack = [item.term, item.definition, ...(item.aliases || [])]
      .join(" ")
      .toLowerCase();
    return haystack.includes(search);
  });
}

export default TRUCKING_DICTIONARY_TERMS;
